import { useRoute, Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Tablet, Lock, RefreshCw, Phone, ShoppingCart, Clock, Settings, Home, Hand } from "lucide-react";

export default function KioskHelp() {
  const [, orgParams] = useRoute("/org/:orgSlug/admin/kiosk-help");
  const orgSlug = orgParams?.orgSlug || "";
  
  const dashboardUrl = orgSlug ? `/org/${orgSlug}/admin` : "/admin";

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container max-w-4xl mx-auto px-4">
        <div className="mb-6">
          <Link href={dashboardUrl}>
            <Button variant="ghost" className="gap-2" data-testid="button-back-dashboard">
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
        </div>

        <div className="space-y-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="text-page-title">
              Kiosk Mode Setup Guide
            </h1>
            <p className="text-gray-600" data-testid="text-page-description">
              Set up a self-service ordering station for your customers
            </p>
          </div>

          <Card data-testid="card-what-is-kiosk">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tablet className="h-5 w-5 text-blue-600" />
                What is Kiosk Mode?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-700">
              <p>
                Kiosk mode transforms an iPad or tablet into a dedicated self-service ordering station. 
                Customers can look up their account by phone number, enter their PIN, and place orders 
                without needing staff assistance.
              </p>
              <p>
                <strong>Best for:</strong> Busy pick-up locations, farmers markets, or anywhere you want 
                customers to place their own orders while you focus on fulfillment.
              </p>
            </CardContent>
          </Card>

          <Card data-testid="card-how-it-works">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-green-600" />
                How Kiosk Mode Works
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                    <Phone className="h-6 w-6 text-blue-600" />
                  </div>
                  <h4 className="font-semibold mb-1">1. Phone Lookup</h4>
                  <p className="text-sm text-gray-600">Customer enters their phone number to find their account</p>
                </div>
                <div className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                    <Lock className="h-6 w-6 text-green-600" />
                  </div>
                  <h4 className="font-semibold mb-1">2. PIN Entry</h4>
                  <p className="text-sm text-gray-600">Secure 4-digit PIN confirms their identity</p>
                </div>
                <div className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-3">
                    <ShoppingCart className="h-6 w-6 text-purple-600" />
                  </div>
                  <h4 className="font-semibold mb-1">3. Place Order</h4>
                  <p className="text-sm text-gray-600">Customer selects items and submits their order</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  <strong>Auto-reset:</strong> After 2 minutes of inactivity, the kiosk automatically 
                  returns to the phone entry screen to protect customer privacy.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="card-launch-kiosk">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-gray-600" />
                Step 1: Launch Kiosk on Your iPad
              </CardTitle>
              <CardDescription>Do this on the iPad you'll use as a kiosk</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-700">
              <ol className="list-decimal list-inside space-y-2">
                <li>On your iPad, log into the admin dashboard in Safari</li>
                <li>Go to your <strong>Orders</strong> page</li>
                <li>Tap the <strong>Actions</strong> menu (top right)</li>
                <li>Select <strong>Launch Kiosk</strong></li>
                <li>The kiosk will open in Safari with the session token in the URL</li>
              </ol>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mt-3">
                <p className="text-sm text-blue-800">
                  <strong>Important:</strong> The kiosk session lasts 30 days. After that, 
                  simply repeat this step to get a fresh session.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="card-ipad-setup">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-5 w-5 text-blue-600" />
                Step 2: Add to Home Screen
              </CardTitle>
              <CardDescription>While the kiosk page is open in Safari</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-700">
              <ol className="list-decimal list-inside space-y-2">
                <li>With the <strong>kiosk page open</strong> in Safari (not the dashboard)</li>
                <li>Tap the <strong>Share</strong> button (square with arrow at bottom)</li>
                <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
                <li>Name it (e.g., "Order Kiosk") and tap <strong>Add</strong></li>
              </ol>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mt-3">
                <p className="text-sm text-amber-800">
                  <strong>Tip:</strong> Make sure you're on the kiosk phone entry page when 
                  adding to home screen, NOT the dashboard. The kiosk URL contains the session 
                  token that makes it work.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="card-guided-access">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-red-600" />
                Step 3: Enable Guided Access (Recommended)
              </CardTitle>
              <CardDescription>Lock the iPad to the kiosk app</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-700">
              <p>
                Guided Access prevents customers from exiting the kiosk or accessing other apps. 
                This is strongly recommended for public-facing kiosks.
              </p>
              
              <div className="space-y-3">
                <h4 className="font-semibold">Enable Guided Access (one-time setup):</h4>
                <ol className="list-decimal list-inside space-y-2">
                  <li>Open iPad <strong>Settings</strong> → <strong>Accessibility</strong></li>
                  <li>Scroll down and tap <strong>Guided Access</strong></li>
                  <li>Toggle <strong>Guided Access</strong> ON</li>
                  <li>Tap <strong>Passcode Settings</strong> and set a passcode (you'll use this to exit)</li>
                </ol>
              </div>

              <div className="space-y-3 pt-2">
                <h4 className="font-semibold flex items-center gap-2">
                  <Hand className="h-4 w-4" />
                  Start Guided Access:
                </h4>
                <ol className="list-decimal list-inside space-y-2">
                  <li>Open the kiosk app from the Home Screen</li>
                  <li><strong>Triple-click the Side button</strong> (or Home button on older iPads)</li>
                  <li>Tap <strong>Start</strong> in the top-right corner</li>
                </ol>
              </div>

              <div className="space-y-3 pt-2">
                <h4 className="font-semibold">Exit Guided Access:</h4>
                <ol className="list-decimal list-inside space-y-2">
                  <li><strong>Triple-click the Side button</strong></li>
                  <li>Enter your Guided Access passcode</li>
                  <li>Tap <strong>End</strong> in the top-left corner</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="card-tips">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-indigo-600" />
                Tips & Troubleshooting
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-indigo-600">•</span>
                  <span><strong>Session expired?</strong> Launch a new kiosk session from the dashboard. Sessions last 24 hours.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-indigo-600">•</span>
                  <span><strong>Kiosk not resetting?</strong> The 2-minute idle timeout resets after any touch. If stuck, refresh the page.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-indigo-600">•</span>
                  <span><strong>Wrong account showing?</strong> Wait 2 minutes for auto-reset, or tap the back button if available.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-indigo-600">•</span>
                  <span><strong>Keep iPad charged:</strong> Use a secure charging stand for all-day operation.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-indigo-600">•</span>
                  <span><strong>Disable Auto-Lock:</strong> In Settings → Display & Brightness → Auto-Lock, select "Never" to keep the screen on.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <div className="text-center pt-4">
            <Link href={dashboardUrl}>
              <Button className="gap-2" data-testid="button-back-dashboard-bottom">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
